import React, { Fragment, useState, useEffect } from "react";
import LoadingBtn from "../../components/LoadingBtn";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import endpoints from "../../api/endpoints";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import EmptyProfile from "../../images/EmptyProfile.webp";

function AccountPanel(props) {
  const [avatar, setAvatar] = useState();
  const [newAvatar, setNewAvatar] = useState();
  const [newAvatarBlob, setNewAvatarBlob] = useState();
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const axiosPrivate = useAxiosPrivate();

  // Upload Profile Picture
  const uploadProfilePictureHandler = async () => {
    if (newAvatar) {
      try {
        setIsUploading(true);

        console.log("Uploading new avatar...");

        // Upload Profile Picture
        let formData = new FormData();
        formData.append("avatar", newAvatarBlob);

        const res = await axiosPrivate.post(endpoints.UPLOAD_AVATAR, formData);

        setAvatar(res.data.url);

        // Reset
        setNewAvatarBlob(null);
        setNewAvatar(null);

        // Set New Profile Picture
        const res2 = await axiosPrivate.patch(endpoints.PROFILE_URL, {
          profilePic: res.data.url,
        });
      } catch (error) {
        console.error(error);
        // navigate('/signin', { state: { from: location }, replace: true });
      } finally {
        setIsUploading(false);
        props.updateHeader();
      }
    }
  };

  const updateUserProfile = async (values) => {
    // Send patch request
    try {
      setIsSaving(true);
      // Delete avatar
      delete values.avatar;
      const res = await axiosPrivate.patch(endpoints.PROFILE_URL, values);
      console.log(res);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
      toast.success("Profile updated successfully!");
    }
  };

  useEffect(() => {
    // Save Changes
    console.log("Save changes...");

    uploadProfilePictureHandler();
  }, [newAvatar]);

  const profileFormSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    region: Yup.string(),
  });

  const profileForm = useFormik({
    initialValues: {
      name: props?.user?.name,
      email: props?.user?.email,
      avatar: props?.user?.avatar,
    },
    validationSchema: profileFormSchema,
    onSubmit: (values) => {
      updateUserProfile(values);
    },
  });

  useEffect(() => {
    // Update avatar
    if (props?.user?.avatar) {
      setAvatar(props?.user?.avatar);
    } else {
      setAvatar(EmptyProfile);
    }
  }, []);

  useEffect(() => {
    console.log("newAvatar: ", newAvatar);
  }, [newAvatar]);

  return (
    <div className="grow">
      <form onSubmit={profileForm.handleSubmit}>
        {/* Panel body */}
        <div className="p-6 space-y-6">
          <h2 className="text-2xl text-black dark:text-white font-bold mb-5">
            My Account
          </h2>
          {/* Picture */}
          <section>
            <div className="flex items-center">
              <div className="group relative mr-4">
                <div className="relative flex items-center justify-center">
                  {isUploading ? (
                    <svg
                      className="z-10 h-1/2 w-1/2 absolute animate-spin fill-current shrink-0"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                    </svg>
                  ) : null}

                  <img
                    className={`w-32 h-32 object-cover transition no-duration ease-in-out rounded-full ${
                      isUploading ? "opacity-50 blur-sm" : ""
                    }`}
                    src={newAvatar ? newAvatar : avatar}
                    width="80"
                    height="80"
                    alt="User upload"
                  />
                </div>
                <div className="h-full w-full absolute top-0 transform translate-x -translate-y">
                  {isUploading ? (
                    <span
                      htmlFor="avatar-upload"
                      className="rounded-none btn-sm w-full invisible group-hover:visible group-hover:bg-black group-hover:text-white transition ease-in-out duration-200"
                    >
                      <svg
                        className="animate-spin w-3 h-3 fill-current shrink-0"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                      </svg>
                      <span className="ml-2">Saving</span>
                    </span>
                  ) : (
                    <label
                      htmlFor="avatar-upload"
                      className="rounded-none btn-sm w-full shadow-none text-transparent group-hover:visible group-hover:bg-black group-hover:text-white transition-all ease-in-out duration-200"
                    >
                      Change
                    </label>
                  )}
                </div>
              </div>
              <div className="space-x-2">
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const fileReader = new FileReader();
                    fileReader.onload = () => {
                      if (fileReader.readyState === 2) {
                        setNewAvatar(fileReader.result);
                        setNewAvatarBlob(e.target.files[0]);
                      }
                    };
                    fileReader.readAsDataURL(e.target.files[0]);
                  }}
                />
              </div>
            </div>
          </section>
          {/* Business Profile */}
          <section>
            <h2 className="text-xl leading-snug text-black dark:text-white font-bold mb-1">
              User Profile
            </h2>

            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              <div className="sm:w-1/2">
                <label
                  className="text-black dark:text-white block text-sm font-medium mb-1"
                  htmlFor="name"
                >
                  Name
                </label>
                <input
                  id="name"
                  className="form-input w-full dark:bg-gray-800 dark:text-white dark:border-gray-600"
                  type="text"
                  onChange={profileForm.handleChange}
                  value={profileForm.values.name}
                />
              </div>
              <div className="sm:w-1/2">
                <label
                  className="text-black dark:text-white block text-sm font-medium mb-1"
                  htmlFor="email"
                >
                  Email
                </label>
                <input
                  id="email"
                  className="form-input w-full dark:bg-gray-800 dark:text-white dark:border-gray-600"
                  type="text"
                  onChange={profileForm.handleChange}
                  value={profileForm.values.email}
                />
              </div>
            </div>
          </section>
        </div>
        {/* Panel footer */}
        <footer>
          <div className="flex flex-col px-6 py-5">
            <div className="flex self-end">
              <span className="ml-3">
                {isSaving ? (
                  <LoadingBtn text="Saving" />
                ) : (
                  <button
                    type="submit"
                    className="btn bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-500 text-white"
                  >
                    Save Changes
                  </button>
                )}
              </span>
            </div>
          </div>
        </footer>
      </form>
    </div>
  );
}

export default AccountPanel;
